import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  useMediaQuery,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Divider,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Autocomplete,
  Grid,
  Stack,
} from "@mui/material";
import dayjs from "dayjs";
import MainLayout from "../components/MainLayout.js";
import backgroundImage from "../assets/backgroundHome.png";
import Constants from "../components/Constant.js";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

export default function HistoricoPrefeitura() {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [title, setTitle] = useState("Abastece Fácil - Prefeitura de ...");
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  const [abastecimentos, setAbastecimentos] = useState([]);
  const [dataInicio, setDataInicio] = useState(dayjs().subtract(10, "day").format("YYYY-MM-DD"));
  const [dataFim, setDataFim] = useState(dayjs().format("YYYY-MM-DD"));
  const currentYear = new Date().getFullYear();
  const minDate = `${currentYear}-01-01`;
  const maxDate = `${currentYear}-12-31`;
  const [combustivelSelecionado, setCombustivelSelecionado] = useState("");
  const [combustiveis, setCombustiveis] = useState([]);
  const [orgaos, setOrgaos] = useState([]);
  const [selectedOrgao, setSelectedOrgao] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const idUsuario = localStorage.getItem("idUsuario") || sessionStorage.getItem("idUsuario");
    const idPrefeitura = localStorage.getItem("idPrefeitura") || sessionStorage.getItem("idPrefeitura");
    const profile = localStorage.getItem("profile") || sessionStorage.getItem("profile");
    const nomePrefeitura = localStorage.getItem("nomePrefeitura") || sessionStorage.getItem("nomePrefeitura");

    if (token) {
      setTitle("Abastece Fácil - Prefeitura de " + nomePrefeitura);
      setUserData({ token, idUsuario, idPrefeitura, profile });
    }
  }, []);

  useEffect(() => {
    if (userData?.token && userData?.idPrefeitura) {
      buscarHistorico();
      buscarOrgaos();
    }
  }, [userData]);

  const buscarOrgaos = async () => {
    try {
      const response = await axios.get(
        `${Constants.API_BASE_URL}/api/orgaosPrefeitura/${userData.idPrefeitura}`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            Accept: "*/*",
          }
        }
      );

      let orgaosArray = [];
      if (response.data.orgaos) {
        orgaosArray = Object.values(response.data.orgaos);
      } else if (Array.isArray(response.data)) {
        orgaosArray = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        orgaosArray = response.data.data;
      }
      setOrgaos(orgaosArray);
    } catch (error) {
      console.error("Erro ao buscar órgãos:", error);
    }
  };

  const buscarHistorico = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${Constants.API_BASE_URL}/api/listarAbastecimentosPrefeitura`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            Accept: "*/*",
          },
          params: {
            PRE_ID: userData.idPrefeitura,
            data_inicio: dataInicio,
            data_fim: dataFim,
          },
        }
      );

      const transformedData = response.data.abastecimentos.map((item) => ({
        dataTime: formatarDataHora(item.ABA_DATA_VINCULACAO),
        numeroCard: numeroCartaoFormatado(item.ABA_CAR_ID),
        combustivel: item.combustivel?.COM_DESCRICAO,
        litrosAbastecido: formatarValor(item.ABA_QNT_LITROS),
        precoLitro: formatarValor(item.ABA_PRECO_LITRO),
        total: formatarValor(item.ABA_VALOR_ABASTECIDO),
      }));

      setAbastecimentos(transformedData);
      setCombustiveis([...new Set(transformedData.map((item) => item.combustivel))]);
    } catch (error) {
      console.error("Erro ao buscar abastecimentos:", error);
      setAbastecimentos([]);
    }
    setLoading(false);
  };

  const downloadHistorico = async () => {
    try {
      const response = await axios.get(
        `${Constants.API_BASE_URL}/api/relatorioDetalhado`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            Accept: "application/pdf",
          },
          params: {
            PRE_ID: userData.idPrefeitura,
            data_inicio: dataInicio,
            data_fim: dataFim,
            tipo: "stream",
            tipo_relatorio: "liquido",
            ORG_ID: selectedOrgao?.ORG_ID || undefined,
          },
          responseType: "blob",
        }
      );
  
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao abrir o PDF:", error);
    }
  };

  const handleFiltrar = () => {
    buscarHistorico();
  };

  const formatarDataHora = (dataISO) => {
    const data = new Date(dataISO);
    const dataFormatada = data.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const horaFormatada = data.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    return `${dataFormatada} às ${horaFormatada}`;
  };

  const numeroCartaoFormatado = (id) => {
    let numCartao = `${id}`;
    if (numCartao.length === 1) return "000" + numCartao;
    else if (numCartao.length === 2) return "00" + numCartao;
    else if (numCartao.length === 3) return "0" + numCartao;
    else return numCartao;
  };

  const formatarValor = (valor) => Number(valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 });

  return (
    <MainLayout titlePage={title} loading={loading}>
      <Box
        sx={{
          flexGrow: 1,
          backgroundImage: `url(${backgroundImage})`,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backgroundBlendMode: "overlay",
          padding: isMobile ? 2 : 3,
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
          Histórico de Abastecimentos
        </Typography>
        
        <Container maxWidth="lg" sx={{ padding: isMobile ? 0 : 2 }}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            {/* Seção de Filtros */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Data inicial"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: minDate, max: maxDate }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Data final"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: minDate, max: maxDate }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Combustível"
                  value={combustivelSelecionado}
                  onChange={(e) => setCombustivelSelecionado(e.target.value)}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {combustiveis.map((tipo, index) => (
                    <MenuItem key={index} value={tipo}>
                      {tipo}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={orgaos}
                  getOptionLabel={(option) => 
                    option ? `${option.ORG_SIGLA} - ${option.ORG_DESCRICAO}` : ""
                  }
                  value={selectedOrgao}
                  onChange={(_, newValue) => setSelectedOrgao(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Órgão (Opcional)"
                      size="small"
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <strong>{option.ORG_SIGLA}</strong> - {option.ORG_DESCRICAO}
                    </li>
                  )}
                />
              </Grid>
            </Grid>

            {/* Botões de Ação */}
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mb: 3 }}>
              <Button
                variant="contained"
                onClick={handleFiltrar}
                startIcon={<FilterAltIcon />}
                sx={{ 
                  px: 4,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Filtrar
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={downloadHistorico}
                startIcon={<PictureAsPdfIcon />}
                sx={{ 
                  px: 4,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Exportar PDF
              </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Exibição dos Dados */}
            {isMobile ? (
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {abastecimentos.length > 0 ? (
                  abastecimentos
                    .filter((item) => !combustivelSelecionado || item.combustivel === combustivelSelecionado)
                    .map((item, index) => (
                      <Card key={index} sx={{ mb: 2, borderRadius: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">
                            {item.dataTime}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <DetailItem label="Credencial" value={item.numeroCard} />
                            <DetailItem label="Combustível" value={item.combustivel} />
                            <DetailItem label="Litros" value={`${item.litrosAbastecido} L`} />
                            <DetailItem label="Preço/Litro" value={`R$ ${item.precoLitro}`} />
                          </Box>
                          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
                            Total: R$ {item.total}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      Nenhum abastecimento encontrado
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: '60vh', border: '1px solid', borderColor: 'divider' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Credencial</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Combustível</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Litros</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Preço/Litro</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {abastecimentos.length > 0 ? (
                      abastecimentos
                        .filter((item) => !combustivelSelecionado || item.combustivel === combustivelSelecionado)
                        .map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{item.dataTime}</TableCell>
                            <TableCell>{item.numeroCard}</TableCell>
                            <TableCell>{item.combustivel}</TableCell>
                            <TableCell align="right">{item.litrosAbastecido} L</TableCell>
                            <TableCell align="right">R$ {item.precoLitro}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 500 }}>R$ {item.total}</TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            Nenhum abastecimento encontrado
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Container>
      </Box>
    </MainLayout>
  );
}

// Componente auxiliar para detalhes nos cards mobile
function DetailItem({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}:
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  );
}